import { useEffect, useRef, useState } from "react";

// First container/codec the current browser can actually record.
// iOS Safari only supports audio/mp4 (AAC); Chrome/Firefox prefer webm/opus.
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/aac",
];

function pickMimeType() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

// Bare container type (no ";codecs=..."), safe to put in a multipart Content-Type.
export function baseMime(mime = "") {
  return (mime.split(";")[0] || "").trim().toLowerCase();
}

export function extensionForMime(mime = "") {
  const m = baseMime(mime);
  if (m.includes("mp4") || m.includes("aac") || m.includes("m4a")) return "m4a";
  if (m.includes("ogg")) return "ogg";
  return "webm";
}

export default function useRecorder() {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioMimeType, setAudioMimeType] = useState("audio/webm");
  const [durationSeconds, setDurationSeconds] = useState(0);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch (e) {}
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      if (audioURL) URL.revokeObjectURL(audioURL);

      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== "closed") {
            audioContextRef.current.close();
          }
        } catch (e) {}
      }
    };
  }, [audioURL]);

  async function startRecording() {
    if (isRecording) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    streamRef.current = stream;
    chunksRef.current = [];

    const picked = pickMimeType();
    const recorder = picked
      ? new MediaRecorder(stream, { mimeType: picked })
      : new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data?.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      // Container the browser actually produced, minus the ";codecs=..." param
      // (that param is not valid in a multipart Content-Type and some upload
      // backends reject it). webm on Chrome/Android, mp4 on iOS Safari.
      const type = baseMime(recorder.mimeType || picked) || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      setAudioBlob(blob);
      setAudioMimeType(type);

      if (audioURL) URL.revokeObjectURL(audioURL);

      const url = URL.createObjectURL(blob);
      setAudioURL(url);

      // Compute duration via AudioContext
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
        const d = Math.round(decoded.duration);
        setDurationSeconds(d);
      } catch (err) {
        setDurationSeconds(0);
      }
    };

    recorder.start(1000);
    setIsRecording(true);
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
  }

  function reset() {
    setAudioBlob(null);
    setAudioURL(null);
    setAudioMimeType("audio/webm");
    setDurationSeconds(0);
    chunksRef.current = [];
  }

  return {
    startRecording,
    stopRecording,
    reset,
    isRecording,
    audioBlob,
    audioURL,
    audioMimeType,
    durationSeconds,
  };
}
